/* Item 3 — Internal note popover.
 *
 * Reusable popover for tender/phase/line-scoped internal notes. Opens
 * from:
 *   • the tender-level Internal Notes section on the Notes tab
 *   • the note glyph in the ref-cell of an estimate LineRow
 *   • the note glyph on a phase header
 *
 * Uses info-slate tokens (`text-info`, `bg-info-soft`, `text-info-deep`)
 * per Correction 4 — no new colour token introduced. Internal/external
 * distinction is carried by layout + Lock icon + explicit labelling,
 * NOT by a bespoke hue.
 */
import React, { useEffect, useState } from "react";
import { X, Plus, Edit3, Trash2, History, Save, Lock, StickyNote } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

// Mirrors backend `_md_to_safe_html` — escape then convert **bold** to <strong>.
function mdToHtml(md) {
  if (!md) return "";
  const escaped = String(md)
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;");
  return escaped.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>");
}

function fmtDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, "0");
    return `${pad(d.getDate())}/${pad(d.getMonth() + 1)}/${d.getFullYear()}`;
  } catch {
    return "";
  }
}

export default function InternalNotePopover({
  tenderId, scope, targetId, targetLabel,
  open, onClose, onChange,
}) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [historyId, setHistoryId] = useState(null);
  const [historyRows, setHistoryRows] = useState([]);

  const reload = React.useCallback(async () => {
    if (!open) return;
    setLoading(true);
    try {
      const r = await api.get(`/tenders/${tenderId}/internal-notes`);
      const grouped = r.data.grouped || { tender: [], by_phase: {}, by_line: {} };
      let items = [];
      if (scope === "tender") items = grouped.tender || [];
      else if (scope === "phase") items = grouped.by_phase?.[targetId] || [];
      else if (scope === "line") items = grouped.by_line?.[targetId] || [];
      setNotes(items);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load notes");
    } finally {
      setLoading(false);
    }
  }, [open, tenderId, scope, targetId]);

  useEffect(() => { reload(); }, [reload]);

  const create = async () => {
    const body = (draft || "").trim();
    if (!body) return;
    try {
      await api.post(`/tenders/${tenderId}/internal-notes`, {
        scope, target_id: targetId, body_md: body,
      });
      setDraft("");
      await reload();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not add note");
    }
  };

  const saveEdit = async (id) => {
    const body = (editingText || "").trim();
    if (!body) return;
    try {
      await api.patch(`/tenders/${tenderId}/internal-notes/${id}`, { body_md: body });
      setEditingId(null);
      setEditingText("");
      await reload();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save edit");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this internal note?")) return;
    try {
      await api.delete(`/tenders/${tenderId}/internal-notes/${id}`);
      await reload();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not delete note");
    }
  };

  const openHistory = async (id) => {
    setHistoryId(id);
    try {
      const r = await api.get(`/tenders/${tenderId}/internal-notes/${id}/history`);
      setHistoryRows(r.data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load history");
    }
  };

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[60] bg-ink/30 flex items-start justify-center pt-24 px-4"
      onClick={onClose}
      data-testid="internal-note-popover-backdrop"
    >
      <div
        className="w-full max-w-lg bg-surface border border-line rounded-lg shadow-lg overflow-hidden"
        onClick={(e) => e.stopPropagation()}
        data-testid="internal-note-popover"
      >
        {/* Header — info-slate accent, lock icon, explicit label */}
        <div className="flex items-center justify-between gap-2 px-4 py-3 bg-info-soft border-b border-line">
          <div className="flex items-center gap-2 text-info-deep">
            <Lock size={13} />
            <span className="font-mono uppercase" style={{ fontSize: 10.5, letterSpacing: "0.08em" }}>
              Internal · never in client documents
            </span>
          </div>
          <button
            onClick={onClose}
            className="text-ink-3 hover:text-ink"
            data-testid="internal-note-popover-close"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-4 py-3 border-b border-line">
          <div className="text-ink font-medium" style={{ fontSize: 13 }}>
            {targetLabel || (scope === "tender" ? "Tender-level notes" : `${scope} notes`)}
          </div>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {loading ? (
            <div className="px-4 py-6 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
          ) : notes.length === 0 ? (
            <div className="px-4 py-6 text-center text-ink-3" style={{ fontSize: 12 }}>
              No internal notes yet. Add one below.
            </div>
          ) : (
            <ul className="divide-y divide-line" data-testid="internal-note-list">
              {notes.map((n) => (
                <li key={n.id} className="px-4 py-3" data-testid={`internal-note-row-${n.id}`}>
                  {editingId === n.id ? (
                    <div className="space-y-2">
                      <textarea
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        rows={3}
                        className="w-full bg-bg border border-line rounded px-2 py-1.5 text-ink"
                        style={{ fontSize: 12 }}
                        data-testid={`internal-note-edit-textarea-${n.id}`}
                      />
                      <div className="flex justify-end gap-2">
                        <button
                          onClick={() => { setEditingId(null); setEditingText(""); }}
                          className="text-ink-3 hover:text-ink"
                          style={{ fontSize: 11.5 }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => saveEdit(n.id)}
                          className="inline-flex items-center gap-1 bg-info text-white rounded px-2 py-1"
                          style={{ fontSize: 11.5 }}
                          data-testid={`internal-note-save-${n.id}`}
                        >
                          <Save size={11} /> Save
                        </button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <div
                        className="text-ink"
                        style={{ fontSize: 12.5, lineHeight: 1.5 }}
                        dangerouslySetInnerHTML={{ __html: mdToHtml(n.body_md) }}
                      />
                      <div className="mt-1.5 flex items-center justify-between">
                        <span className="text-ink-3" style={{ fontSize: 10.5 }}>
                          {(n.updated_by_name || n.created_by_name || "—")} · {fmtDate(n.updated_at || n.created_at)}
                        </span>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => openHistory(n.id)}
                            className="inline-flex items-center gap-1 text-ink-3 hover:text-info"
                            style={{ fontSize: 11 }}
                            data-testid={`internal-note-history-${n.id}`}
                          >
                            <History size={11} /> History
                          </button>
                          <button
                            onClick={() => { setEditingId(n.id); setEditingText(n.body_md); }}
                            className="inline-flex items-center gap-1 text-ink-3 hover:text-info"
                            style={{ fontSize: 11 }}
                            data-testid={`internal-note-edit-${n.id}`}
                          >
                            <Edit3 size={11} /> Edit
                          </button>
                          <button
                            onClick={() => del(n.id)}
                            className="inline-flex items-center gap-1 text-ink-3 hover:text-red-700"
                            style={{ fontSize: 11 }}
                            data-testid={`internal-note-delete-${n.id}`}
                          >
                            <Trash2 size={11} /> Delete
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Add new */}
        <div className="px-4 py-3 border-t border-line bg-surface-2/40">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={2}
            placeholder="Add an internal note (bold with **markdown**). Never appears in client documents."
            className="w-full bg-bg border border-line rounded px-2 py-1.5 text-ink"
            style={{ fontSize: 12 }}
            data-testid="internal-note-add-textarea"
          />
          <div className="mt-2 flex justify-end">
            <button
              onClick={create}
              disabled={!draft.trim()}
              className="inline-flex items-center gap-1 bg-info text-white rounded px-3 py-1.5 disabled:opacity-40"
              style={{ fontSize: 11.5 }}
              data-testid="internal-note-add-btn"
            >
              <Plus size={11} /> Add note
            </button>
          </div>
        </div>

        {/* History panel — inline expansion */}
        {historyId ? (
          <div className="border-t border-line bg-surface" data-testid={`internal-note-history-panel-${historyId}`}>
            <div className="px-4 py-2 flex items-center justify-between bg-info-soft">
              <div className="flex items-center gap-2 text-info-deep">
                <History size={11} />
                <span style={{ fontSize: 11 }}>History</span>
              </div>
              <button
                onClick={() => { setHistoryId(null); setHistoryRows([]); }}
                className="text-info-deep hover:text-ink"
                style={{ fontSize: 11 }}
              >
                Close
              </button>
            </div>
            <div className="max-h-40 overflow-y-auto px-4 py-2">
              {historyRows.length === 0 ? (
                <div className="text-ink-3 text-center py-2" style={{ fontSize: 11 }}>No history yet.</div>
              ) : (
                <ul className="space-y-1.5">
                  {historyRows.map((h, i) => (
                    <li key={h.id || i} className="text-ink-2" style={{ fontSize: 11 }}>
                      <div className="flex items-center gap-2 text-ink-3">
                        <span>{fmtDate(h.timestamp)}</span>
                        <span>·</span>
                        <span>{h.user_name || "—"}</span>
                      </div>
                      <div className="text-ink">{h.description}</div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

/* Convenience — the small clickable glyph shown on line rows and phase
 * headers. Uses `text-info` so it visually matches every other note-adjacent
 * indicator in the app (prelim notes, promote-to-library, LibraryEdit info). */
export function InternalNoteGlyph({ count, onClick, testId }) {
  const has = (count || 0) > 0;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(); }}
      title={has ? `${count} internal note${count === 1 ? "" : "s"}` : "Add internal note"}
      className={
        "inline-flex items-center gap-0.5 rounded px-1 py-0.5 " +
        (has ? "text-info hover:bg-info-soft" : "text-ink-4 hover:text-info hover:bg-info-soft/60") +
        " transition-colors"
      }
      data-testid={testId}
    >
      <StickyNote size={11} />
      {has ? <span className="font-mono" style={{ fontSize: 10 }}>{count}</span> : null}
    </button>
  );
}
